import type React from "react";

import { useEffect, useCallback, useRef } from "react";
import { useCommandBar } from "@/components/command-bar/command-bar-context";

interface SearchOptions {
  searchFn: (query: string) => Promise<any[]>;
  transformResult: (result: any) => {
    id: string;
    label: string;
    description?: string;
    icon?: React.ReactNode;
    onSelect: () => void;
    keywords?: string[];
  };
  debounceMs?: number;
  minQueryLength?: number;
}

export function useCommandBarSearch({
  searchFn,
  transformResult,
  debounceMs = 800,
  minQueryLength = 3,
}: SearchOptions) {
  const { searchQuery, setSearchResults, searchResults } = useCommandBar();
  const timeoutRef = useRef<NodeJS.Timeout>();

  const performSearch = useCallback(
    async (query: string) => {
      if (query.length < minQueryLength) {
        setSearchResults([]);
        return;
      }

      try {
        const results = await searchFn(query);
        const transformedResults = results.map(transformResult);
        setSearchResults(transformedResults);
      } catch (error) {
        console.error("[v0] Search failed:", error);
        setSearchResults([]);
      }
    },
    [searchFn, transformResult],
  );

  useEffect(() => {
    if (searchQuery) {
      timeoutRef.current = setTimeout(() => {
        performSearch(searchQuery);
      }, debounceMs);
    } else {
      if (searchResults.length > 0) setSearchResults([]);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [searchQuery]);
}
